import React, { useState, useEffect, useRef } from 'react';

import { IVideoProps } from '../Video';
import useScript from '../../utils/hooks/use-script';
import formatJWPlayerId from '../utils/format-jwplayer-id';
import { BASE_AD_URL, NO_CAPTIONS_FLAG } from '../constants';

import {
  EPageType,
  IArticleGlobalData,
  IJWPlayer,
  IJWPlayerOptions,
  IJWPlayerTrack,
} from '../../types';
import { EJWPlayerBucket } from '../types';

export interface IJWPlayerProps {
  externalId: IVideoProps['externalId'];
  adTargets?: IVideoProps['adTargets'];
}

const JWPlayer: React.FC<IJWPlayerProps> = ({ externalId, adTargets }) => {
  // split the single externalId string into its separate components
  const { videoId, playerId, captionsId, videoBucket } = formatJWPlayerId(
    externalId
  );

  // track both the element to which we attach the player
  // and the player instance itself
  const jwElRef = useRef<HTMLDivElement | null>(null);
  const jwPlayerRef = useRef<IJWPlayer | null>(null);

  // used to track the currently loaded player script in case
  // this component re-renders with a new playerId
  const [loadedPlayerId, setLoadedPlayerId] = useState<string | null>(null);

  // whether the JW script has loaded
  const { isReady: scriptIsReady } = useScript({
    url: `https://content.jwplatform.com/libraries/${playerId}.js?ver=1.0.1`,
  });

  // if we sent in an explicit ad-targeting prop, use that
  // otherwise, check if we're on an article page and use the values
  // stored on window.ttGlobal
  // if neither case is true, return an empty string
  const getAdString = (): string => {
    if (!adTargets && window.ttGlobal.pageType !== EPageType.Article) {
      return BASE_AD_URL;
    }

    const { series = [], gutenTags = [], contentId = '' } =
      adTargets || (window.ttGlobal as IArticleGlobalData);

    const targetingString = `cust_params=vidseries%3D${series.join(
      '%2C'
    )}%26vidid%3D${videoId}%26vidarticleid%3D${contentId}%26vidguten%3D${gutenTags.join(
      '%2C'
    )}`;

    return `${BASE_AD_URL}&${targetingString}`;
  };

  const getFile = (): string => {
    if (videoBucket === EJWPlayerBucket.Livestream) {
      return `//content.jwplatform.com/videos/${videoId}.m3u8`;
    }
    if (videoBucket === EJWPlayerBucket.Recorded) {
      return `//content.jwplatform.com/manifests/${videoId}.m3u8`;
    }

    return '';
  };

  const getTracks = (): IJWPlayerTrack[] => {
    const tracks: IJWPlayerTrack[] = [];

    if (captionsId !== NO_CAPTIONS_FLAG) {
      tracks.push({
        file: `https://content.jwplatform.com/tracks/${captionsId}.vtt`,
        label: 'English',
      });
    }

    return tracks;
  };

  const getConfig = (): IJWPlayerOptions => {
    const config: IJWPlayerOptions = {
      advertising: {
        client: 'googima',
        tag: getAdString(),
        enablepreloading: true,
      },
      autostart: false,
      file: getFile(),
      id: playerId,
      image: `//content.jwplatform.com/thumbs/${videoId}-720.jpg`,
      mediaid: videoId,
      mute: false,
      tracks: getTracks(),
    };

    return config;
  };

  const buildPlayer = (): void => {
    if (jwElRef.current) {
      const config = getConfig();
      const player = window.jwplayer(jwElRef.current).setup(config);

      jwPlayerRef.current = player;
    }
  };

  // mark when the script for the latest playerId prop has loaded and executed
  useEffect(
    (): void => {
      if (scriptIsReady) {
        setLoadedPlayerId(playerId);
      }
    },
    [scriptIsReady, playerId]
  );

  useEffect(
    (): void => {
      if (loadedPlayerId !== playerId && jwPlayerRef.current) {
        jwPlayerRef.current.remove();
        jwPlayerRef.current = null;
      }
    }
  );

  useEffect(
    (): void => {
      if (loadedPlayerId === playerId && !jwPlayerRef.current) {
        buildPlayer();
      }
    }
  );

  return <div ref={jwElRef} data-testid="jw-attach" />;
};

export default JWPlayer;
